/**
 * RoleAwareOnboardingEntry — single /onboarding entry point.
 *
 * Dispatches to the correct onboarding by user.role so nothing overlaps:
 *   • team_member      → /welcome (employee orientation)
 *   • country_operator → Country Operator wizard
 *   • client_admin     → Subscriber Admin wizard (Sprint 2)
 *   • super_admin      → Subscriber Admin wizard (so they can demo / preview it)
 *   • anything else    → subscriber admin wizard as safe default
 *
 * When a user CLICKS the sidebar "Setup Wizard" link, they want to see
 * a wizard — never redirect them away.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingWizardPage from './OnboardingWizardPage';
import CountryOperatorOnboardingPage from './CountryOperatorOnboardingPage';

export default function RoleAwareOnboardingEntry() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Employees have their own dedicated /welcome orientation. Everyone else
    // stays on this page and sees the appropriate wizard.
    if (!loading && user && user.role === 'team_member') {
      navigate('/welcome', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-gray-400 text-sm">Preparing your setup…</div>
      </div>
    );
  }

  if (user.role === 'country_operator') {
    return <CountryOperatorOnboardingPage />;
  }
  // client_admin, super_admin (for demo/preview), and anything else → subscriber wizard.
  return <OnboardingWizardPage />;
}
